/**
 * useBreakPageExperiment
 *
 * Thin composable that connects the experiment store to the tracking service
 * for the Break Page A/B experiment.
 *
 * Responsibilities:
 * - Get the break page variant (defaults to 'control' unless opted-in via URL)
 * - Fire VARIANT_ASSIGNED only for opted-in users
 * - Expose `isBreakEnabled` computed ref
 * - Provide `trackPhase2ContinueGoal()` to fire CONVERSION_GOAL_REACHED
 */

import { computed, ref } from 'vue';
import {
	useExperimentStore,
	BREAK_PAGE_EXPERIMENT,
} from '../stores/coordination/useExperimentStore';
import { useTracking } from '../services/tracking';
import { TrackingEventType } from '@monorepo/types';

export function useBreakPageExperiment() {
	const experimentStore = useExperimentStore();
	const tracking = useTracking();

	// Track whether we have already fired the VARIANT_ASSIGNED event this session.
	// Using a module-level ref to survive component re-renders but reset on page reload.
	const assignmentTracked = ref(false);

	/**
	 * Initialize the experiment and fire tracking if the user was opted-in.
	 * Only fires VARIANT_ASSIGNED for users who activated the experiment via URL params.
	 */
	function initExperiment(): string {
		const variantId = experimentStore.getOrAssignVariant(BREAK_PAGE_EXPERIMENT);

		// Only track assignment for users who opted-in via URL params
		// (not for default-variant users who never activated the experiment)
		if (
			!assignmentTracked.value &&
			experimentStore.isExperimentActivated(BREAK_PAGE_EXPERIMENT.id)
		) {
			assignmentTracked.value = true;

			tracking.trackExperimentEvent({
				eventType: TrackingEventType.VARIANT_ASSIGNED,
				experimentId: BREAK_PAGE_EXPERIMENT.id,
				variantId,
				metadata: {
					experimentId: BREAK_PAGE_EXPERIMENT.id,
					variantId,
					source: 'useBreakPageExperiment',
				},
			});
		}

		return variantId;
	}

	/**
	 * Whether the break page should be shown for this user.
	 */
	const isBreakEnabled = computed<boolean>(() => {
		const variantId = initExperiment();
		return variantId === BREAK_PAGE_EXPERIMENT.variants.BREAK;
	});

	/**
	 * Track that the conversion goal was reached (user started phase 2).
	 * Only fires for opted-in users.
	 */
	function trackPhase2ContinueGoal(): void {
		// Only track for opted-in users
		if (!experimentStore.isExperimentActivated(BREAK_PAGE_EXPERIMENT.id)) return;

		const variantId = experimentStore.assignments[BREAK_PAGE_EXPERIMENT.id] ?? 'unknown';

		tracking.trackExperimentEvent({
			eventType: TrackingEventType.CONVERSION_GOAL_REACHED,
			experimentId: BREAK_PAGE_EXPERIMENT.id,
			variantId,
			goalName: 'phase2_started',
			metadata: {
				experimentId: BREAK_PAGE_EXPERIMENT.id,
				variantId,
				goalName: 'phase2_started',
			},
		});
	}

	return {
		isBreakEnabled,
		initExperiment,
		trackPhase2ContinueGoal,
	};
}
